import { useState } from "react";
import { mockInfluencers } from "@/mocks/influencers";

const platformIcon: Record<string, string> = {
  tiktok: "ri-tiktok-line",
  youtube: "ri-youtube-line",
  twitter: "ri-twitter-x-line",
  instagram: "ri-instagram-line",
  facebook: "ri-facebook-line",
  douyin: "ri-tiktok-line",
  weixin: "ri-wechat-line",
};

const platformLabel: Record<string, string> = {
  tiktok: "TikTok",
  youtube: "YouTube",
  twitter: "Twitter/X",
  instagram: "Instagram",
  facebook: "Facebook",
  douyin: "Douyin",
  weixin: "WeChat",
};

const categoryColors: Record<string, string> = {
  "Tech & Lifestyle": "#B8860B",
  "Beauty & Skincare": "#C2185B",
  "Fitness & Sports": "#2E7D32",
  "Food & Home": "#E65100",
  "Fashion & Style": "#6A1B9A",
  "Lifestyle & Home": "#00695C",
  "Electronics & Gadgets": "#1565C0",
  "Fashion & Beauty": "#AD1457",
  "Home & Garden": "#558B2F",
};

const rankMedal = (rank: number) => {
  if (rank === 1) return { bg: "#FFD700", text: "#3D2000", label: "🥇" };
  if (rank === 2) return { bg: "#C0C0C0", text: "#1a1a1a", label: "🥈" };
  if (rank === 3) return { bg: "#CD7F32", text: "#fff", label: "🥉" };
  return { bg: "rgba(61,32,0,0.1)", text: "#5C3D00", label: `#${rank}` };
};

const sortedInfluencers = [...mockInfluencers].sort((a, b) => {
  const aVal = parseFloat(a.earnings.replace(/[$,K]/g, "")) * (a.earnings.includes("K") ? 1000 : 1);
  const bVal = parseFloat(b.earnings.replace(/[$,K]/g, "")) * (b.earnings.includes("K") ? 1000 : 1);
  return bVal - aVal;
});

const FILTERS = ["All", "TikTok", "YouTube", "Twitter/X", "Instagram", "Facebook"];

export default function InfluencerLeaderboard() {
  const [activeFilter, setActiveFilter] = useState("All");

  const filtered = activeFilter === "All"
    ? sortedInfluencers
    : sortedInfluencers.filter((inf) => platformLabel[inf.platform] === activeFilter);

  return (
    <section
      className="py-16 px-6 md:px-12 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #B8860B 0%, #DAA520 25%, #FFD700 50%, #DAA520 75%, #B8860B 100%)" }}
    >
      {/* Shimmer overlay */}
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.15) 10px, rgba(255,255,255,0.15) 20px)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4"
            style={{ background: "rgba(61,32,0,0.2)", color: "#3D2000" }}
          >
            Leaderboard
          </span>
          <h2 className="text-4xl font-extrabold mb-3" style={{ color: "#3D2000" }}>
            Top Earning Influencers
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "#6B4500" }}>
            Real commissions. Real creators. See who's crushing it on StarBookPrime this month and get inspired to join them.
          </p>
        </div>

        {/* Platform filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className="px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={
                activeFilter === f
                  ? { background: "#3D2000", color: "#FFD700" }
                  : { background: "rgba(255,255,255,0.35)", color: "#3D2000", border: "1px solid rgba(61,32,0,0.2)" }
              }
            >
              {f}
            </button>
          ))}
        </div>

        {/* Top 3 podium */}
        {activeFilter === "All" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {sortedInfluencers.slice(0, 3).map((inf, i) => {
              const medal = rankMedal(i + 1);
              return (
                <div
                  key={inf.id}
                  className={`rounded-2xl p-6 text-center relative border transition-transform hover:scale-105 cursor-pointer ${i === 0 ? "md:order-2" : i === 1 ? "md:order-1" : "md:order-3"}`}
                  style={{ background: "rgba(255,255,255,0.35)", borderColor: "rgba(255,255,255,0.6)" }}
                >
                  {/* Rank badge */}
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold"
                    style={{ background: medal.bg, color: medal.text }}
                  >
                    {i + 1}
                  </div>
                  <img
                    src={inf.avatar}
                    alt={inf.name}
                    className="w-20 h-20 rounded-full object-cover object-top mx-auto mb-3 border-4"
                    style={{ borderColor: medal.bg }}
                  />
                  <div className="font-bold text-base mb-1" style={{ color: "#3D2000" }}>{inf.name}</div>
                  <div className="text-xs mb-2 flex items-center justify-center gap-1" style={{ color: "#6B4500" }}>
                    <i className={platformIcon[inf.platform]} />
                    <span>{inf.handle}</span>
                  </div>
                  <div
                    className="inline-block text-xs px-2 py-1 rounded-full mb-3 font-medium"
                    style={{ background: `${categoryColors[inf.category] || "#B8860B"}22`, color: categoryColors[inf.category] || "#B8860B" }}
                  >
                    {inf.category}
                  </div>
                  <div className="text-2xl font-extrabold mb-1" style={{ color: "#3D2000" }}>{inf.earnings}</div>
                  <div className="text-xs mb-2" style={{ color: "#6B4500" }}>earned this month</div>
                  <div className="flex justify-center gap-4 text-xs" style={{ color: "#5C3D00" }}>
                    <span><strong>{inf.followers}</strong> followers</span>
                    <span><strong>{inf.commission}</strong> rate</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full leaderboard table */}
        <div className="rounded-2xl overflow-hidden border" style={{ background: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.5)" }}>
          {/* Table header */}
          <div
            className="grid grid-cols-12 gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider"
            style={{ background: "rgba(61,32,0,0.15)", color: "#3D2000" }}
          >
            <div className="col-span-1 text-center">Rank</div>
            <div className="col-span-4">Creator</div>
            <div className="col-span-2">Platform</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1 text-right">Followers</div>
            <div className="col-span-1 text-right">Rate</div>
            <div className="col-span-1 text-right">Earned</div>
          </div>

          {filtered.map((inf, i) => {
            const globalRank = sortedInfluencers.findIndex((x) => x.id === inf.id) + 1;
            const medal = rankMedal(globalRank);
            return (
              <div
                key={inf.id}
                className="grid grid-cols-12 gap-2 px-5 py-4 items-center border-t transition-colors hover:bg-white/20 cursor-pointer"
                style={{ borderColor: "rgba(255,255,255,0.3)" }}
              >
                {/* Rank */}
                <div className="col-span-1 flex justify-center">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold"
                    style={{ background: medal.bg, color: medal.text }}
                  >
                    {globalRank}
                  </div>
                </div>
                {/* Creator */}
                <div className="col-span-4 flex items-center gap-3">
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <img src={inf.avatar} alt={inf.name} className="w-10 h-10 rounded-full object-cover object-top" />
                    {inf.verified && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full" style={{ background: "#3D2000" }}>
                        <i className="ri-check-line text-xs" style={{ color: "#FFD700", fontSize: "9px" }} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "#3D2000" }}>{inf.name}</div>
                    <div className="text-xs" style={{ color: "#6B4500" }}>{inf.handle}</div>
                  </div>
                </div>
                {/* Platform */}
                <div className="col-span-2 flex items-center gap-1 text-sm" style={{ color: "#5C3D00" }}>
                  <i className={platformIcon[inf.platform]} />
                  <span className="text-xs">{platformLabel[inf.platform]}</span>
                </div>
                {/* Category */}
                <div className="col-span-2">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: `${categoryColors[inf.category] || "#B8860B"}22`, color: categoryColors[inf.category] || "#B8860B" }}
                  >
                    {inf.category}
                  </span>
                </div>
                {/* Followers */}
                <div className="col-span-1 text-right text-sm font-semibold" style={{ color: "#3D2000" }}>{inf.followers}</div>
                {/* Rate */}
                <div className="col-span-1 text-right">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(61,32,0,0.15)", color: "#3D2000" }}>
                    {inf.commission}
                  </span>
                </div>
                {/* Earned */}
                <div className="col-span-1 text-right text-sm font-bold" style={{ color: "#3D2000" }}>{inf.earnings}</div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <p className="text-sm mb-4" style={{ color: "#5C3D00" }}>
            These creators started just like you. Apply below and start earning today.
          </p>
          <a
            href="#influencer-apply"
            className="inline-flex items-center gap-2 px-8 py-4 font-bold rounded-full transition-all hover:scale-105 cursor-pointer text-base whitespace-nowrap"
            style={{ background: "#3D2000", color: "#FFD700" }}
          >
            <i className="ri-trophy-line" />
            Join the Leaderboard
          </a>
        </div>
      </div>
    </section>
  );
}
